
import { Component, ElementRef, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild, ChangeDetectorRef } from '@angular/core';
import { NgbActiveModal, NgbNavModule } from '@ng-bootstrap/ng-bootstrap';
import { TreeNode } from '../../../../core/interfaces/tree-node.interface';
import { DataContextService, MapEntity } from '../../../../core/services/data-context.service';
import { WindowService } from '../../../../core/services/window.service';
import { ImageryLayersService } from '../../../../core/services/wrench-services/imagery-layers.service';
import { TreeNodeComponent } from '../tree-node/tree-node.component';
import { Subscription } from 'rxjs';

export interface Layer {
  id: string;
  name: string;
  type: string;
  url: string;
  visible: boolean;
  opacity: number;
}

@Component({
  selector: 'app-data-entry-panel',
  standalone: true,
  imports: [NgbNavModule, TreeNodeComponent],
  templateUrl: './data-entry-panel.component.html',
  styleUrl: './data-entry-panel.component.css'
})
export class DataEntryPanelComponent implements OnInit, OnDestroy {
  @ViewChild('layersTree', { static: false }) layersTreeRef!: ElementRef;

  isOpen = false;
  activeTab = 'entities';
  treeData: TreeNode[] = [];
  providers: any[] = [];

  private readonly UNKNOWN_BIN = 'KML';
  private entitiesChangedSub?: Subscription;

  constructor(
    private dataContext: DataContextService,
    private imageryLayers: ImageryLayersService,
    private windowService: WindowService,
    private cdr: ChangeDetectorRef,
    public activeModal: NgbActiveModal
  ) {}

  ngOnInit(): void {
    this.providers = this.imageryLayers.getImageryLayers();
    this.entitiesChangedSub = this.dataContext.entitiesChanged$.subscribe(() => {
      this.buildTree();
      this.cdr.detectChanges();
    });
    this.buildTree();
  }

  ngOnDestroy(): void {
    this.entitiesChangedSub?.unsubscribe();
  }

  toggleOpenCloseLayers(): void {
    if (!this.isOpen) {
      this.buildTree();
      this.isOpen = true;
    } else {
      this.isOpen = false;
    }
  }

  closeLayerUi(): void {
    this.activeModal.dismiss();
  }

  setActiveTab(tab: string): void {
    this.activeTab = tab;
  }

  trackByNodeId(index: number, node: TreeNode): string {
    return node.nodeId || `node-${index}`;
  }

  onNodeSelected(node: TreeNode): void {
    if (node.mapEntity) {
      if (node.mapEntity.goToEntityOnSelection()) {
        node.mapEntity.goToEntity();
      }
      if (node.mapEntity.openEntityWindownOnSelection()) {
        const compiledContent = node.mapEntity.getDetailsWindow();
        this.windowService.openMapEntityDetailsWindow(node.mapEntity.getWindowId(), compiledContent);
      }
    }
  }

  onNodeChecked(node: TreeNode): void {
    if (node.mapEntity) {
      this.showHideMapEntity(node.mapEntity, true);
    } else {
      this.checkChildNodes(node, true);
    }
    this.checkParentNode(node, true);
  }

  onNodeUnchecked(node: TreeNode): void {
    if (node.mapEntity) {
      this.showHideMapEntity(node.mapEntity, false);
    } else {
      this.checkChildNodes(node, false);
    }
    this.uncheckParentNode(node);
  }

  private showHideMapEntity(mapEntity: MapEntity, show: boolean): void {
    if (show) {
      mapEntity.show();
    } else {
      mapEntity.hide();
    }
  }

  private checkChildNodes(node: TreeNode, checked: boolean): void {
    if (node.nodes) {
      node.nodes.forEach(childNode => {
        childNode.state.checked = checked;
        if (childNode.mapEntity) {
          this.showHideMapEntity(childNode.mapEntity, checked);
        }
        if (childNode.nodes && childNode.nodes.length > 0) {
          this.checkChildNodes(childNode, checked);
        }
      });
    }
  }

  private checkParentNode(node: TreeNode, checked: boolean): void {
    // Find parent and update its state based on children
    // This would require parent references or tree traversal
    // Implementation depends on how you want to handle parent-child relationships
  }

  private uncheckParentNode(node: TreeNode): void {
    // Similar to checkParentNode but for unchecking
    // Check if any siblings are still checked before unchecking parent
  }

  private trim(str: string): string {
    return str.trim();
  }

  private addLabelNode(label: string, shown: boolean): TreeNode | undefined {
    if (!label) return undefined;
    return {
      text: label,
      state: { checked: shown },
      nodes: [],
      nodeId: `label-${Math.random().toString(36).substr(2, 9)}`
    };
  }

  private addMapEntityNode(mapEntity: any): TreeNode | undefined {
    if (!mapEntity || !mapEntity.name) return undefined;
    const checked = typeof mapEntity.isVisible === 'function'
      ? mapEntity.isVisible()
      : !!(mapEntity.cesiumEntity && mapEntity.cesiumEntity.show);
    return {
      text: mapEntity.name,
      state: { checked },
      mapEntity: mapEntity,
      nodeId: `entity-${mapEntity.getId ? mapEntity.getId() : mapEntity.id}`
    };
  }

  private buildDefaultTreeBin(tree: any): void {
    if (!(this.UNKNOWN_BIN in tree)) {
      tree[this.UNKNOWN_BIN] = { nodes: [] };
    }
  }

  private addToDefaultTreeBin(tree: any, mapEntity: MapEntity): void {
    this.buildDefaultTreeBin(tree);
    tree[this.UNKNOWN_BIN].nodes.push(
      this.addMapEntityNode(mapEntity)
    );
  }

  private nodeToUITree(node: TreeNode | undefined, mapTree: any): void {
    if (!node) return;
    // Add nodes if they exist
    if (mapTree.nodes) {
      mapTree.nodes.forEach((childNode: TreeNode) => {
        if (childNode && childNode.text !== undefined) {
          node.nodes!.push(childNode);
          if (childNode.state.checked) {
            node.state.checked = true;
          }
        }
      });
      node.tags = [node.nodes!.length];
    }
    // Process nested levels
    Object.keys(mapTree).forEach(key => {
      if (key !== 'nodes' && key !== 'ArcAngel') {
        const newNode = this.addLabelNode(key, false);
        if (newNode) {
          this.nodeToUITree(newNode, mapTree[key]);
          if (newNode.text !== undefined) {
            node.nodes!.push(newNode);
          }
        }
      }
    });
  }

  private mapTreeToUITree(tree: TreeNode[], mapTree: any): TreeNode[] {
    Object.keys(mapTree).forEach(key => {
      if (key !== 'nodes' && key !== 'ArcAngel') {
        const node = this.addLabelNode(key, false);
        if (node) {
          this.nodeToUITree(node, mapTree[key]);
          if (node.text !== undefined) {
            tree.push(node);
          }
        }
      }
    });
    return tree;
  }

  private expandAllNodes(nodes: any[]): void {
    if (!nodes) return;
    for (const node of nodes) {
      if (!node.state) node.state = {};
      node.state.expanded = true;
      if (node.nodes && node.nodes.length > 0) {
        this.expandAllNodes(node.nodes);
      }
    }
  }

  private buildTree(): void {
    const mapTree: any = {};
    const entities = this.dataContext.getEntities();
    for (let i = 0; i < entities.length; i++) {
      if (entities[i] === undefined) {
        continue;
      }
      const mapEntity = entities[i].arcLiveMapEntity || entities[i];
      if (!mapEntity.layerPath || mapEntity.layerPath.length < 1) {
        this.addToDefaultTreeBin(mapTree, mapEntity);
      } else {
        const layers = mapEntity.layerPath.split('//');
        if (this.trim(layers[0]).length < 1) {
          this.addToDefaultTreeBin(mapTree, mapEntity);
        } else {
          let currentLayer = this.trim(layers[0]);
          if (!(currentLayer in mapTree)) {
            mapTree[currentLayer] = {};
          }
          let currentTreeBranch = mapTree[currentLayer];
          for (let j = 1; j < layers.length; j++) {
            currentLayer = this.trim(layers[j]);
            if (!(currentLayer in currentTreeBranch)) {
              currentTreeBranch[currentLayer] = {};
            }
            currentTreeBranch = currentTreeBranch[currentLayer];
          }
          if (!('nodes' in currentTreeBranch)) {
            currentTreeBranch.nodes = [];
          }
          const node = this.addMapEntityNode(mapEntity);
          if (node) {
            currentTreeBranch.nodes.push(node);
          }
        }
      }
    }
    const tree: any[] = [];
    this.treeData = this.mapTreeToUITree(tree, mapTree);
    this.expandAllNodes(this.treeData);
  }
}